import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { SextantFigure } from "../../ui/SextantFigure";

interface ReadingProps {
  onNavigate: (to: string) => void;
}

const MODULE = "astro";
const GAME = "reading";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

interface Card {
  degrees: number; // whole degrees on the arc
  minutes: number; // micrometer, one-decimal
  correctStr: string;
  choices: readonly string[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function fmtReading(deg: number, min: number): string {
  return `${deg}° ${min.toFixed(1)}′`;
}

function buildCard(): Card {
  // Plausible sights — well above the horizon and well below the
  // sextant's awkward upper end.
  const degrees = randInt(8, 78);
  // 0.0..59.9 with one-decimal precision; skip very near 0 and 60
  // (boundaries are visually ambiguous for a learning drill).
  const wholeMin = randInt(1, 58);
  const tenths = randInt(0, 9);
  const minutes = wholeMin + tenths / 10;

  const correctStr = fmtReading(degrees, minutes);
  const seen = new Set<string>([correctStr]);
  const variants: Array<{ d: number; m: number }> = [
    { d: Math.max(0, degrees - 1), m: minutes }, // off-by-one degree (low)
    { d: Math.min(89, degrees + 1), m: minutes }, // off-by-one degree (high)
    { d: degrees, m: (minutes + 10) % 60 }, // misread minute by 10
    { d: degrees, m: Math.max(0, Math.min(59.9, 60 - minutes)) }, // mirror
    { d: degrees, m: (minutes + 30) % 60 }, // half-turn drum
  ];
  const distractors: string[] = [];
  for (const v of variants) {
    if (distractors.length >= CHOICES_PER_CARD - 1) break;
    const s = fmtReading(v.d, v.m);
    if (!seen.has(s)) {
      seen.add(s);
      distractors.push(s);
    }
  }

  const choices = [correctStr, ...distractors];
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = choices[i]!;
    choices[i] = choices[j]!;
    choices[j] = a;
  }

  return { degrees, minutes, correctStr, choices };
}

export function Reading({ onNavigate }: ReadingProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === card.correctStr;
      const attempt: Omit<Attempt, "module"> = {
        char: "reading",
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/astro")}
      />
    );
  }

  const revealed = chosen !== null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "astro" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>What does the sextant read?</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          The arm pointer on the top scale gives the whole degrees; the
          drum pointer on the lower scale gives the minutes (to a tenth).
        </p>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
          <SextantFigure
            degrees={card.degrees}
            minutes={card.minutes}
            showReading={revealed}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c === card.correctStr;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 18,
                  padding: "14px 0",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.correctStr
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.correctStr
                ? `✓ Correct — ${card.correctStr}.`
                : `✗ Not quite — ${card.correctStr}.`}
            </p>
            <p className="small muted" style={{ margin: "6px 0 0" }}>
              Read the arc to the integer degree, then the micrometer
              gives the minutes and tenths.
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next reading →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/astro")}>
        ← Back to Astronavigation
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Astronavigation
        </button>
      </div>
    </>
  );
}
